import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

interface StepDotsProps {
  /** Total number of steps */
  totalSteps: number;
  /** Current step (0-indexed) */
  currentStep: number;
  /** Additional className */
  className?: string;
}

/**
 * Step progress indicator using animated dots.
 * Current step is highlighted with bank theme color.
 */
export const StepDots: React.FC<StepDotsProps> = ({
  totalSteps,
  currentStep,
  className,
}) => {
  return (
    <div className={cn("flex items-center justify-center gap-1.5", className)}>
      {Array.from({ length: totalSteps }).map((_, index) => {
        const isCompleted = index < currentStep;
        const isCurrent = index === currentStep;
        const isUpcoming = index > currentStep;

        return (
          <motion.div
            key={index}
            className={cn(
              "rounded-full transition-colors duration-200",
              isCompleted && "bg-bank-primary",
              isCurrent && "bg-bank-primary",
              isUpcoming && "bg-muted-foreground/30",
            )}
            initial={false}
            animate={{
              width: isCurrent ? 24 : 6,
              height: 6,
              opacity: isUpcoming ? 0.5 : 1,
            }}
            transition={{
              type: "spring",
              stiffness: 500,
              damping: 30,
            }}
          />
        );
      })}
    </div>
  );
};

/**
 * Alternative: Simple line progress indicator
 */
export const StepProgress: React.FC<{
  progress: number;
  className?: string;
}> = ({ progress, className }) => {
  return (
    <div
      className={cn(
        "h-1 w-full bg-muted rounded-full overflow-hidden",
        className,
      )}
    >
      <motion.div
        className="h-full bg-bank-primary rounded-full"
        initial={{ width: 0 }}
        animate={{ width: `${progress}%` }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 30,
        }}
      />
    </div>
  );
};

export default StepDots;
